import { Platform } from 'react-native';

export type LiveOmrScanResult = {
  status: 'success' | 'no_qr' | 'error';
  message: string;
  job_id?: string | null;
  image_key?: string | null;
  school_id?: string | null;
  batch_id?: string | null;
  question_count?: number | null;
  question_set_id?: string | null;
  qr_payload?: {
    qc?: number;
    rn?: string;
    sid?: string;
    sig?: string;
    v?: string;
  } | null;
  student?: {
    name: string;
    roll: string;
  } | null;
  evaluation?: {
    accuracy: string;
    score: number;
    total: number;
  } | null;
  omr?: {
    summary: {
      attempted_count: number;
      blank_count: number;
      ambiguous_count: number;
      multi_marked_count: number;
    };
    questions: Array<{
      question_number: number;
      status: string;
      selected_option: string | null;
      confidence: number;
      bubble_scores: Array<{
        option: string;
        filled_score: number;
        darkness_ratio: number;
        center_x: number;
        center_y: number;
      }>;
    }>;
  } | null;
  debug?: {
    failure_reason?: string | null;
    anchor_count?: number;
    anchor_source?: string;
    bubble_overlay_bucket?: string | null;
    bubble_overlay_key?: string | null;
    bubble_overlay_url?: string | null;
    used_page_contour_fallback?: boolean;
    qr_source?: string;
    saved_dir?: string;
    manifest_path?: string;
  } | null;
  scanned_at?: string;
};

export type OmrPreviewPoint = {
  quadrant: 'top_left' | 'top_right' | 'bottom_right' | 'bottom_left';
  selected: boolean;
  x_norm: number;
  y_norm: number;
};

export type OmrPreviewResult = {
  status: 'ready' | 'partial' | 'none' | 'error';
  message: string;
  question_set_id?: string | null;
  image_width?: number;
  image_height?: number;
  anchor_count?: number;
  failure_reason?: string | null;
  points?: OmrPreviewPoint[];
};

type OmrUploadIntentResult = {
  status: 'success' | 'error';
  message: string;
  image_key?: string;
  upload_url?: string;
  method?: 'PUT';
  headers?: Record<string, string>;
  public_url?: string | null;
  expires_in_seconds?: number;
  school_id?: string;
  question_count?: number;
  batch_id?: string | null;
};

type OmrJobResult = {
  job_id: string;
  status: 'queued' | 'processing' | 'success' | 'no_qr' | 'error' | 'failed' | 'not_found';
  message?: string;
  request?: {
    image_key: string;
    school_id: string;
    question_count: number;
    batch_id?: string | null;
  };
  result?: LiveOmrScanResult;
  error?: string;
};

const omrApiUrl = process.env.EXPO_PUBLIC_OMR_API_URL?.trim();

export function isOmrApiConfigured() {
  return Boolean(omrApiUrl);
}

function resolveOmrApiUrl() {
  if (!omrApiUrl) return null;

  // Android emulators cannot reach the host machine via localhost/127.0.0.1.
  if (
    Platform.OS === 'android' &&
    (omrApiUrl.includes('://127.0.0.1') || omrApiUrl.includes('://localhost'))
  ) {
    return omrApiUrl
      .replace('://127.0.0.1', '://10.0.2.2')
      .replace('://localhost', '://10.0.2.2');
  }

  return omrApiUrl;
}

function buildUploadUri(uri: string) {
  if (Platform.OS === 'android' || uri.startsWith('file://')) {
    return uri;
  }

  return uri;
}

async function readLocalFileAsBlob(uri: string) {
  const response = await fetch(buildUploadUri(uri));
  if (!response.ok) {
    throw new Error(`Unable to read local image file (${response.status}).`);
  }
  return response.blob();
}

async function uploadImageToPresignedUrl(
  imageUri: string,
  uploadUrl: string,
  headers: Record<string, string>,
) {
  const blob = await readLocalFileAsBlob(imageUri);
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers,
    body: blob,
  });

  if (!response.ok) {
    throw new Error(`R2 upload failed with status ${response.status}.`);
  }
}

async function queueOmrScanFromMultipart(input: {
  imageUri: string;
  schoolId: string;
  questionCount: number;
  batchId?: string | null;
}) {
  const baseUrl = resolveOmrApiUrl();

  if (!baseUrl) {
    throw new Error(
      'OMR API URL is missing. Set EXPO_PUBLIC_OMR_API_URL in your Expo environment.',
    );
  }

  const blob = await readLocalFileAsBlob(input.imageUri);
  const formData = new FormData();
  formData.append('image', blob, 'omr-upload.jpg');
  formData.append('school_id', input.schoolId);
  formData.append('question_count', String(input.questionCount));
  if (input.batchId) {
    formData.append('batch_id', input.batchId);
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl.replace(/\/+$/, '')}/api/omr/scan/live`, {
      method: 'POST',
      body: formData,
    });
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Unknown network error.';
    throw new Error(`Unable to reach OMR API at ${baseUrl}. ${detail}`);
  }

  const payload = (await response.json()) as {
    status: string;
    message?: string;
    job_id?: string;
    image_key?: string;
  };

  if (!response.ok) {
    throw new Error(payload.message || 'OMR multipart queue request failed.');
  }
  if (payload.status !== 'queued' || !payload.job_id) {
    throw new Error(payload.message || 'Scan job was not queued.');
  }

  return payload;
}

export async function createOmrScanJobFromUri(input: {
  imageUri: string;
  schoolId: string;
  questionCount: number;
  batchId?: string | null;
}) {
  if (Platform.OS === 'web') {
    const queueResult = await queueOmrScanFromMultipart(input);
    const jobId = queueResult.job_id;
    if (!jobId) {
      throw new Error('Queue response was missing the job id.');
    }

    return {
      imageKey: queueResult.image_key ?? null,
      jobId,
      queueResult,
      uploadIntent: null,
    };
  }

  const uploadIntent = await requestOmrUploadIntent({
    schoolId: input.schoolId,
    questionCount: input.questionCount,
    batchId: input.batchId,
  });

  const uploadUrl = uploadIntent.upload_url;
  const imageKey = uploadIntent.image_key;
  if (!uploadUrl || !imageKey) {
    throw new Error('Upload intent response was missing the upload URL or image key.');
  }

  await uploadImageToPresignedUrl(
    input.imageUri,
    uploadUrl,
    uploadIntent.headers ?? {
      'Content-Type': 'image/jpeg',
    },
  );

  const queueResult = await queueOmrScan({
    imageKey,
    schoolId: input.schoolId,
    questionCount: input.questionCount,
    batchId: input.batchId,
  });

  const jobId = queueResult.job_id;
  if (!jobId) {
    throw new Error('Queue response was missing the job id.');
  }

  return {
    imageKey,
    jobId,
    queueResult,
    uploadIntent,
  };
}

export async function requestOmrUploadIntent(input: {
  schoolId: string;
  questionCount: number;
  batchId?: string | null;
}) {
  const baseUrl = resolveOmrApiUrl();

  if (!baseUrl) {
    throw new Error(
      'OMR API URL is missing. Set EXPO_PUBLIC_OMR_API_URL in your Expo environment.',
    );
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl.replace(/\/+$/, '')}/api/omr/uploads/presign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        school_id: input.schoolId,
        question_count: input.questionCount,
        batch_id: input.batchId ?? null,
      }),
    });
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Unknown network error.';
    throw new Error(`Unable to reach OMR API at ${baseUrl}. ${detail}`);
  }

  const payload = (await response.json()) as OmrUploadIntentResult;
  if (!response.ok) {
    throw new Error(payload.message || 'Upload intent request failed.');
  }
  if (payload.status !== 'success' || !payload.image_key || !payload.upload_url) {
    throw new Error(payload.message || 'Upload intent was not created.');
  }

  return payload;
}

export async function queueOmrScan(input: {
  imageKey: string;
  schoolId: string;
  questionCount: number;
  batchId?: string | null;
}) {
  const baseUrl = resolveOmrApiUrl();

  if (!baseUrl) {
    throw new Error(
      'OMR API URL is missing. Set EXPO_PUBLIC_OMR_API_URL in your Expo environment.',
    );
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl.replace(/\/+$/, '')}/api/omr/scan/live`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image_key: input.imageKey,
        school_id: input.schoolId,
        question_count: input.questionCount,
        batch_id: input.batchId ?? null,
      }),
    });
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Unknown network error.';
    throw new Error(`Unable to reach OMR API at ${baseUrl}. ${detail}`);
  }

  const payload = (await response.json()) as {
    status: string;
    message?: string;
    job_id?: string;
  };

  if (!response.ok) {
    throw new Error(payload.message || 'OMR queue request failed.');
  }
  if (payload.status !== 'queued' || !payload.job_id) {
    throw new Error(payload.message || 'Scan job was not queued.');
  }

  return payload;
}

export async function getOmrJobStatus(jobId: string) {
  const baseUrl = resolveOmrApiUrl();

  if (!baseUrl) {
    throw new Error(
      'OMR API URL is missing. Set EXPO_PUBLIC_OMR_API_URL in your Expo environment.',
    );
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl.replace(/\/+$/, '')}/api/omr/jobs/${jobId}`, {
      method: 'GET',
    });
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Unknown network error.';
    throw new Error(`Unable to reach OMR API at ${baseUrl}. ${detail}`);
  }

  const payload = (await response.json()) as OmrJobResult;
  if (!response.ok) {
    throw new Error(payload.message || 'Unable to fetch OMR job status.');
  }

  return payload;
}

async function waitForOmrJobCompletion(input: {
  jobId: string;
  pollIntervalMs?: number;
  timeoutMs?: number;
}) {
  const pollIntervalMs = input.pollIntervalMs ?? 1500;
  const timeoutMs = input.timeoutMs ?? 120000;
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const job = await getOmrJobStatus(input.jobId);
    if (job.status === 'success' || job.status === 'no_qr') {
      if (job.result) return job.result;
      throw new Error('OMR job completed without a result payload.');
    }
    if (job.status === 'error' || job.status === 'failed') {
      return {
        status: 'error',
        message: job.error || job.message || 'OMR scan failed.',
      } as LiveOmrScanResult;
    }
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }

  throw new Error('Timed out waiting for OMR processing to finish.');
}

export async function scanOmrLive(input: {
  imageUri: string;
  schoolId: string;
  questionCount: number;
  batchId?: string | null;
}) {
  const baseUrl = resolveOmrApiUrl();

  if (!baseUrl) {
    throw new Error(
      'OMR API URL is missing. Set EXPO_PUBLIC_OMR_API_URL in your Expo environment.',
    );
  }

  const uploadIntent = await requestOmrUploadIntent({
    schoolId: input.schoolId,
    questionCount: input.questionCount,
    batchId: input.batchId,
  });

  const uploadUrl = uploadIntent.upload_url;
  const imageKey = uploadIntent.image_key;
  if (!uploadUrl || !imageKey) {
    throw new Error('Upload intent response was missing the upload URL or image key.');
  }

  await uploadImageToPresignedUrl(
    input.imageUri,
    uploadUrl,
    uploadIntent.headers ?? {
      'Content-Type': 'image/jpeg',
    },
  );

  const queueResult = await queueOmrScan({
    imageKey,
    schoolId: input.schoolId,
    questionCount: input.questionCount,
    batchId: input.batchId,
  });

  const jobId = queueResult.job_id;
  if (!jobId) {
    throw new Error('Queue response was missing the job id.');
  }

  const result = await waitForOmrJobCompletion({
    jobId,
  });

  return result;
}

export async function previewOmrLive(input: {
  imageUri: string;
  questionSetId: string;
}) {
  const baseUrl = resolveOmrApiUrl();

  if (!baseUrl) {
    throw new Error(
      'OMR API URL is missing. Set EXPO_PUBLIC_OMR_API_URL in your Expo environment.',
    );
  }

  const formData = new FormData();
  formData.append('question_set_id', input.questionSetId);
  formData.append(
    'image',
    {
      uri: buildUploadUri(input.imageUri),
      name: 'omr-preview.jpg',
      type: 'image/jpeg',
    } as any,
  );

  let response: Response;
  try {
    response = await fetch(`${baseUrl.replace(/\/+$/, '')}/api/omr/scan/preview`, {
      method: 'POST',
      body: formData,
    });
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Unknown network error.';
    throw new Error(`Unable to reach OMR API at ${baseUrl}. ${detail}`);
  }

  const payload = (await response.json()) as OmrPreviewResult;

  if (!response.ok) {
    throw new Error(payload.message || 'OMR preview request failed.');
  }

  return payload;
}
